import { ExternalLink, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import type { AdminCreditEntry, AdminOrgBilling } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { Button } from '@/components/ui/button';
import {
  creditsSummary,
  invoicesPill,
  invoicesSummary,
  lastGrantLine,
  type MoneyPill,
  planPeriodLine,
  planStatusPill,
  promoSummary,
} from '@/lib/orgMoney';
import type { MoneyPanel } from '@/lib/orgNav';

function MoneyCard({
  id,
  label,
  headline,
  pill,
  details,
  actions,
}: {
  id?: string;
  label: string;
  headline: ReactNode;
  pill?: MoneyPill;
  details?: Array<string | undefined>;
  actions: ReactNode;
}) {
  const lines = (details ?? []).filter((line): line is string => Boolean(line));
  return (
    <section id={id} aria-label={label} className="flex scroll-mt-20 flex-col rounded-xl border border-border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1.5 truncate text-base font-semibold tracking-tight text-foreground">{headline}</p>
      {pill ? (
        <div className="mt-2">
          <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
        </div>
      ) : null}
      {lines.map((line) => (
        <p key={line} className="mt-1.5 truncate text-xs text-muted-foreground" title={line}>
          {line}
        </p>
      ))}
      <div className="mt-auto flex flex-wrap gap-2 pt-4">{actions}</div>
    </section>
  );
}

export function OrgMoneyCards({
  billing,
  lastGrant,
  portalBusy,
  onOpenPanel,
  onOpenPortal,
}: {
  billing?: AdminOrgBilling;
  lastGrant?: AdminCreditEntry;
  portalBusy: boolean;
  onOpenPanel: (panel: MoneyPanel) => void;
  onOpenPortal: () => void;
}) {
  const hasCustomer = Boolean(billing?.stripeCustomerId);
  const promo = billing?.appliedPromo;
  const invoices = billing?.invoices ?? [];

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <MoneyCard
        label="Plan"
        headline={billing?.productName || 'No plan'}
        pill={planStatusPill(billing)}
        details={[planPeriodLine(billing), promo ? promoSummary(promo) : undefined]}
        actions={
          <>
            {hasCustomer ? (
              <Button type="button" variant="outline" size="sm" onClick={onOpenPortal} disabled={portalBusy}>
                {portalBusy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                Manage in Stripe
              </Button>
            ) : null}
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenPanel('plan')}>
              {hasCustomer ? 'Change plan' : 'Set up plan'}
            </Button>
          </>
        }
      />
      <MoneyCard
        id="credits"
        label="Credits"
        headline={creditsSummary(billing)}
        details={[lastGrantLine(lastGrant)]}
        actions={
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenPanel('grant-credit')}>
              Grant credit
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenPanel('history')}>
              History
            </Button>
          </>
        }
      />
      <MoneyCard
        label="Promos"
        headline={promoSummary(promo)}
        details={['Takes money off the Stripe invoice, not Studio credit']}
        actions={
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenPanel('promo')}>
            Apply promo
          </Button>
        }
      />
      <MoneyCard
        label="Invoices"
        headline={invoicesSummary(invoices)}
        pill={invoicesPill(invoices)}
        actions={
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenPanel('invoices')}>
            View invoices
          </Button>
        }
      />
    </div>
  );
}
